import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; faker: any }
    | { kind: 'error'; message: string };

interface State {
    provider: string;
    count: number;
    customDomain: string;
    output: string;
    lib: LibState;
    copied: boolean;
}

const PROVIDERS = ['random', 'gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'protonmail.com', 'custom'];

export default class FakeEmailGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        provider: 'random',
        count: 10,
        customDomain: 'example.com',
        output: '',
        lib: { kind: 'idle' },
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureFaker(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.faker;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const { faker } = await import('@faker-js/faker');
            this.setState({ lib: { kind: 'ready', faker } });
            return faker;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load faker' } });
            return null;
        }
    }

    generate = async () => {
        const faker = await this.ensureFaker();
        if (!faker) return;
        const lines: string[] = [];
        const { provider, count, customDomain } = this.state;
        for (let i = 0; i < count; i++) {
            if (provider === 'random') {
                lines.push(faker.internet.email().toLowerCase());
            } else if (provider === 'custom') {
                const local = faker.internet.username().toLowerCase().replace(/[^a-z0-9._-]/g, '');
                lines.push(`${local}@${customDomain || 'example.com'}`);
            } else {
                const local = faker.internet.username().toLowerCase().replace(/[^a-z0-9._-]/g, '');
                lines.push(`${local}@${provider}`);
            }
        }
        this.setState({ output: lines.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { provider, count, customDomain, output, lib, copied } = this.state;
        const emailCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.field}>
                    <label htmlFor="fe-provider">Provider / domain</label>
                    <select id="fe-provider" value={provider}
                        onChange={e => this.setState({ provider: e.target.value }, this.generate)}>
                        {PROVIDERS.map(p => <option key={p} value={p}>{p === 'random' ? 'Random mix' : p === 'custom' ? 'Custom domain…' : p}</option>)}
                    </select>
                </div>
                {provider === 'custom' && (
                    <div className={genStyles.field}>
                        <label htmlFor="fe-domain">Custom domain</label>
                        <input id="fe-domain" type="text" value={customDomain}
                            placeholder="example.com"
                            onChange={e => this.setState({ customDomain: e.target.value }, this.generate)} />
                    </div>
                )}
                <div className={genStyles.field}>
                    <label htmlFor="fe-count">Number of emails</label>
                    <input id="fe-count" type="number" inputMode="decimal" min={1} max={500} value={count}
                        onChange={e => this.setState({ count: Math.max(1, Math.min(500, +e.target.value || 10)) }, this.generate)} />
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                    disabled={lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                    {lib.kind === 'loading' ? 'Loading faker…' : '↻ Regenerate'}
                </button>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    These are not real email addresses. Use for form testing only — sending mail to them may bounce or hit live users.
                </p>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated emails"
                previewMeta={`${emailCount} email${emailCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated emails">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
                error={lib.kind === 'error' ? (lib as any).message : null}
            />
        );
    }
}
